import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useFormStore } from 'src/domain/stores/useFormStore';
import logError from 'src/data/services/logError';
import errorHandler from 'src/core/utils/errorHandler';
import firstErrorMessage from 'src/core/utils/firstErrorMessage';
import showAlert from 'src/core/utils/showAlert';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import AlertHelper from 'src/presentation/helpers/AlertHelper';
import { isApiError } from 'src/core/models/api/ApiError';
import type { ApiOnBehalfRegistration } from 'src/core/models/api/ApiOnBehalfRegistration';

interface OnBehalfRegistrationsProps {
	eventUuid: string;
	formId: string | number;
	// Bumped by the form after it registered someone, so the list picks up the new entry.
	refreshKey: number;
}

function OnBehalfRegistrations({ eventUuid, formId, refreshKey }: OnBehalfRegistrationsProps) {
	const [registrations, setRegistrations] = useState<ApiOnBehalfRegistration[]>([]);
	const [reloadKey, setReloadKey] = useState(0);
	const [cancellingId, setCancellingId] = useState<string | null>(null);

	useEffect(() => {
		let stale = false;
		useFormStore
			.getState()
			.listOnBehalf(eventUuid)
			.then((list) => {
				// The endpoint lists the whole event; a page can carry more than one form.
				if (!stale) setRegistrations(list.filter((item) => String(item.form_id) === String(formId)));
			})
			.catch(logError);
		return () => {
			stale = true;
		};
	}, [eventUuid, formId, refreshKey, reloadKey]);

	const cancel = (registration: ApiOnBehalfRegistration) => {
		setCancellingId(registration.id);
		useFormStore
			.getState()
			.cancelOnBehalf({ formId: registration.form_id, id: registration.id })
			.then((response) => {
				if (response.status === 'ok') {
					showAlert({
						title: Translations.successUnsubscribedTitle,
						message: Translations.onBehalfCancelledMessage({ name: registration.name }),
					});
				} else {
					errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
				}
			})
			.catch((err: unknown) => {
				// A passed cancellation deadline comes back as a 422 whose message says so.
				const message = isApiError(err) ? firstErrorMessage(err.response?.data?.error) : null;
				if (message) {
					errorHandler(message, null);
				} else {
					logError(err);
					errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
				}
			})
			.finally(() => {
				setCancellingId(null);
				setReloadKey((key) => key + 1);
			});
	};

	const confirmCancel = (registration: ApiOnBehalfRegistration) =>
		AlertHelper.showConfirmAlert({
			title: Translations.alertUnsubscribeTitle,
			message: Translations.onBehalfCancelMessage({ name: registration.name }),
			confirmText: Translations.textUnsubscribe,
			confirmButtonStyle: 'destructive',
			onConfirm: () => cancel(registration),
			cancelable: true,
		});

	if (registrations.length === 0) {
		return null;
	}

	return (
		<View style={styles.container}>
			<Text style={styles.title}>{Translations.onBehalfListTitle}</Text>
			{registrations.map((registration) => (
				<View key={registration.id} style={styles.row}>
					<View style={styles.details}>
						<Text style={styles.name}>{registration.name}</Text>
						<Text style={styles.meta}>{registration.email}</Text>
						<Text style={styles.meta}>
							{`${Translations.mySubmissionRegistrationLabel}: ${registration.registration_number}`}
						</Text>
					</View>
					<TouchableOpacity disabled={cancellingId !== null} onPress={() => confirmCancel(registration)}>
						<Text style={[styles.textLink, cancellingId === registration.id ? styles.textLinkDisabled : null]}>
							{Translations.textUnsubscribe}
						</Text>
					</TouchableOpacity>
				</View>
			))}
		</View>
	);
}

export default OnBehalfRegistrations;

const styles = StyleSheet.create({
	container: {
		marginTop: Dimens.margin24,
	},
	title: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
		paddingBottom: Dimens.padding8,
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyBorder,
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: Dimens.padding12,
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyBorder,
	},
	details: {
		flex: 1,
		paddingRight: Dimens.padding12,
	},
	name: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
	meta: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
		color: Colors.greyText,
		marginTop: Dimens.margin4,
	},
	textLink: {
		fontSize: Dimens.fontSize14,
		color: Colors.secondary,
		fontFamily: Fonts.montserratMedium,
	},
	textLinkDisabled: {
		opacity: 0.4,
	},
});
